import React, { useEffect, useState } from 'react';
import { Tab } from '@headlessui/react';
import {
  CalendarIcon,
  ClockIcon,
  CheckCircleIcon,
  PlusIcon,
  HeartIcon,
  ClipboardDocumentListIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import { healthCheckupsApi, HealthCheckup } from '../../api/healthCheckups';
import { healthRemindersApi, HealthReminder } from '../../api/healthReminders';
import HealthChecklistsList from './HealthChecklistsList';
import HealthAssessmentsList from './HealthAssessmentsList';
import { HealthCareView } from './healthManagementNav';

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ');
}

interface HealthCarePlansPanelProps {
  view: HealthCareView | string;
  onScheduleCheckup: () => void;
  onScheduleReminder: () => void;
  onStartChecklist: () => void;
  onStartAssessment: () => void;
  onInvestigateFromChecklist?: (birdId: string, birdLabel: string, itemIds: string[]) => void;
}

function priorityColor(priority: string) {
  switch (priority) {
    case 'URGENT':
    case 'HIGH':
      return 'text-red-700 bg-red-50';
    case 'MEDIUM':
      return 'text-amber-700 bg-amber-50';
    default:
      return 'text-slate-600 bg-slate-50';
  }
}

function SchedulesPanel({
  onScheduleCheckup,
  onScheduleReminder,
}: {
  onScheduleCheckup: () => void;
  onScheduleReminder: () => void;
}) {
  const [checkups, setCheckups] = useState<HealthCheckup[]>([]);
  const [reminders, setReminders] = useState<HealthReminder[]>([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      setLoading(true);
      const [checkupData, reminderData] = await Promise.all([
        healthCheckupsApi.getAll().catch(() => []),
        healthRemindersApi.getAll().catch(() => []),
      ]);
      setCheckups(checkupData);
      setReminders(reminderData);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleCompleteReminder = async (id: string) => {
    try {
      await healthRemindersApi.markAsCompleted(id);
      toast.success('Reminder completed');
      await load();
    } catch {
      toast.error('Failed to complete reminder');
    }
  };

  const handleCompleteCheckup = async (id: string) => {
    try {
      await healthCheckupsApi.markAsCompleted(id);
      toast.success('Checkup marked complete');
      await load();
    } catch {
      toast.error('Failed to complete checkup');
    }
  };

  const scheduledCheckups = checkups.filter((c) => c.status === 'SCHEDULED' || c.status === 'IN_PROGRESS');
  const activeReminders = reminders.filter((r) => r.status === 'ACTIVE');

  if (loading) {
    return (
      <div className="flex h-48 items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-cyan-600" />
      </div>
    );
  }

  return (
    <Tab.Group>
      <Tab.List className="flex gap-2">
        <Tab className={({ selected }) => classNames('rounded-lg px-4 py-2 text-sm font-medium', selected ? 'bg-cyan-600 text-white' : 'bg-white text-slate-600')}>
          Checkups ({scheduledCheckups.length})
        </Tab>
        <Tab className={({ selected }) => classNames('rounded-lg px-4 py-2 text-sm font-medium', selected ? 'bg-cyan-600 text-white' : 'bg-white text-slate-600')}>
          Reminders ({activeReminders.length})
        </Tab>
      </Tab.List>
      <Tab.Panels className="mt-4">
        <Tab.Panel>
          <div className="mb-3 flex justify-end">
            <button type="button" onClick={onScheduleCheckup} className="btn btn-secondary text-sm">
              <PlusIcon className="h-4 w-4" /> Schedule checkup
            </button>
          </div>
          {scheduledCheckups.length === 0 ? (
            <div className="rounded-lg border border-gray-200 py-10 text-center text-sm text-slate-500">No scheduled checkups.</div>
          ) : (
            <div className="space-y-3">
              {scheduledCheckups.map((checkup) => (
                <div key={checkup.id} className="flex items-center justify-between rounded-lg border border-gray-200 bg-white p-4">
                  <div>
                    <p className="font-medium text-slate-900">{checkup.bird?.bandId || checkup.birdId}</p>
                    <p className="text-sm text-slate-600">{new Date(checkup.checkupDate).toLocaleDateString()}</p>
                    <span className={`mt-1 inline-block rounded-full px-2 py-0.5 text-xs ${priorityColor(checkup.priority)}`}>{checkup.priority}</span>
                  </div>
                  <button type="button" onClick={() => handleCompleteCheckup(checkup.id)} className="btn btn-secondary text-xs">
                    <CheckCircleIcon className="h-4 w-4" /> Complete
                  </button>
                </div>
              ))}
            </div>
          )}
        </Tab.Panel>
        <Tab.Panel>
          <div className="mb-3 flex justify-end">
            <button type="button" onClick={onScheduleReminder} className="btn btn-secondary text-sm">
              <PlusIcon className="h-4 w-4" /> Add reminder
            </button>
          </div>
          {activeReminders.length === 0 ? (
            <div className="rounded-lg border border-gray-200 py-10 text-center text-sm text-slate-500">No active reminders.</div>
          ) : (
            <div className="space-y-3">
              {activeReminders.map((reminder) => (
                <div key={reminder.id} className="flex items-center justify-between rounded-lg border border-gray-200 bg-white p-4">
                  <div>
                    <p className="font-medium text-slate-900">{reminder.title}</p>
                    <p className="text-sm text-slate-600">Due {new Date(reminder.reminderDate).toLocaleDateString()}</p>
                  </div>
                  <button type="button" onClick={() => handleCompleteReminder(reminder.id)} className="btn btn-secondary text-xs">
                    <HeartIcon className="h-4 w-4" /> Done
                  </button>
                </div>
              ))}
            </div>
          )}
        </Tab.Panel>
      </Tab.Panels>
    </Tab.Group>
  );
}

export default function HealthCarePlansPanel({
  view,
  onScheduleCheckup,
  onScheduleReminder,
  onStartChecklist,
  onStartAssessment,
  onInvestigateFromChecklist,
}: HealthCarePlansPanelProps) {
  if (view === 'checklists') {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium text-slate-900">Health checklists</h3>
            <p className="text-sm text-slate-500">Daily, weekly, monthly, and emergency cage-round records.</p>
          </div>
          <button type="button" onClick={onStartChecklist} className="btn btn-primary text-sm">
            <ClipboardDocumentListIcon className="h-4 w-4" /> Run checklist
          </button>
        </div>
        <HealthChecklistsList onInvestigateFromChecklist={onInvestigateFromChecklist} />
      </div>
    );
  }

  if (view === 'assessments') {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium text-slate-900">Health assessments</h3>
            <p className="text-sm text-slate-500">Structured multi-step evaluations with scores and recommendations.</p>
          </div>
          <button type="button" onClick={onStartAssessment} className="btn btn-primary text-sm">
            <MagnifyingGlassIcon className="h-4 w-4" /> New assessment
          </button>
        </div>
        <HealthAssessmentsList />
      </div>
    );
  }

  // schedules (default for care when not investigation/workflows)
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <button type="button" onClick={onScheduleCheckup} className="rounded-lg border border-gray-200 bg-white p-4 text-left hover:border-cyan-300">
          <CalendarIcon className="h-5 w-5 text-cyan-600" />
          <p className="mt-2 text-sm font-medium">Schedule checkup</p>
        </button>
        <button type="button" onClick={onScheduleReminder} className="rounded-lg border border-gray-200 bg-white p-4 text-left hover:border-cyan-300">
          <ClockIcon className="h-5 w-5 text-amber-600" />
          <p className="mt-2 text-sm font-medium">Add reminder</p>
        </button>
        <button type="button" onClick={onStartChecklist} className="rounded-lg border border-gray-200 bg-white p-4 text-left hover:border-cyan-300">
          <ClipboardDocumentListIcon className="h-5 w-5 text-green-600" />
          <p className="mt-2 text-sm font-medium">Run checklist</p>
        </button>
        <button type="button" onClick={onStartAssessment} className="rounded-lg border border-gray-200 bg-white p-4 text-left hover:border-cyan-300">
          <MagnifyingGlassIcon className="h-5 w-5 text-purple-600" />
          <p className="mt-2 text-sm font-medium">Assessment</p>
        </button>
      </div>
      <SchedulesPanel onScheduleCheckup={onScheduleCheckup} onScheduleReminder={onScheduleReminder} />
    </div>
  );
}
